import { gql } from "@apollo/client";

export const FETCH_PRODUCT_IMAGES = gql`
  query FETCH_PRODUCT_IMAGES($id: ID!) {
    product(id: $id) {
      id
      images: computedImages(size: "300x300") {
        id
        thumb: url
      }
    }
  }
`;

export const QC_REPORT_FRAGMENT = gql`
  fragment qcReport on QCReportType {
    id
    name
    factory {
      id
      name
    }
    snapshotSuppliers {
      id
      name
      nickName
    }
    shipment {
      edges {
        node {
          id
          customer {
            id
            name
          }
        }
      }
    }
    invoice {
      id
      number
      customer {
        id
        name
      }
    }
    so
    transfer
    po
    product {
      id
      name
      number
      children {
        product {
          id
          name
          productLine {
            id
            name
          }
          defaultSupplier {
            id
            name
          }
        }
      }
      defaultSupplier {
        id
        name
      }
      productLine {
        id
        name
      }
      images: computedImages(size: "300x300") {
        id
        url
      }
      barcodeInnerboxEan13: innerBoxBarcodeEan
      barcodeInnerboxUpc: innerBoxBarcodeUpc
      barcodeOuterboxEan13: innerCartonBarcodeEan
      barcodeOuterboxUpc: innerCartonBarcodeUpc
      barcodeCarton1: outerCartonBarcodeUpc
      barcodeCarton2: outerCartonBarcodeEan
    }
    qty
    checkQty
    barcodeInnerboxEan13
    barcodeInnerboxUpc
    barcodeOuterboxEan13
    barcodeOuterboxUpc
    barcodeCarton
    status
    qualityCheck
    packageCheck
    logoCheck
    metaImages: images {
      id
      src
      meta {
        id
        name
      }
    }
    qualityIssues {
      id
      defectCode {
        id
        code
        description
        category
      }
      defectType
      qty
      images {
        id
        src
      }
      factory {
        id
        name
      }
      product {
        id
        name
      }
    }
    printings: padPrintings {
      id
      isValid
      images {
        id
        src
      }
    }
    sharedWithCustomerAt
    createdAt
  }
`;

export const FETCH_QC_REPORTS_LIST = gql`
  query FETCH_QC_REPORTS_LIST(
    $limit: Int
    $offset: Int
    $q: String
    $status: String
    $sourceType: String
    $recentRange: String
  ) {
    qcReports(
      options: { limit: $limit, offset: $offset, q: $q }
      status: $status
      sourceType: $sourceType
      recentRange: $recentRange
    ) {
      total
      results {
        id
        factory {
          name
        }
        snapshotSuppliers {
          id
          name
          nickName
        }
        product {
          name
          number
          defaultSupplier {
            name
          }
          packingFactory {
            name
          }
          productLine {
            name
          }
          images: computedImages(size: "300x300") {
            id
            url
          }
        }
        qty
        status
        so
        transfer
        po
        customerOdooId
        customer {
          id
          name
        }
        user {
          email
        }
        createdAt
        deletedAt
        sharedWithCustomerAt
      }
    }
  }
`;

export const FETCH_QC_REPORT = gql`
  query FETCH_QC_REPORT($id: ID!) {
    report: qcReport(id: $id) {
      ...qcReport
    }
  }
  ${QC_REPORT_FRAGMENT}
`;

export const FETCH_PO_QC_REPORT = gql`
  query FETCH_PO_QC_REPORT($id: ID!) {
    report: qcReport(id: $id) {
      id
      name
      po
      qty
      checkQty
      qualityCheck
      createdAt
      product {
        id
        name
        number
        productLine {
          name
        }
        defaultSupplier {
          name
        }
        children {
          product {
            id
            name
            defaultSupplier {
              name
            }
          }
        }
        images: computedImages(size: "300x300") {
          id
          url
        }
      }
      qualityIssues {
        id
        defectCode {
          id
          code
          description
          category
        }
        defectType
        qty
        images {
          id
          src
        }
        factory {
          name
        }
        product {
          id
          name
        }
      }
    }
  }
`;

export const FETCH_QC_PREVIEW = gql`
  query FETCH_QC_PREVIEW($id: ID!) {
    report: qcReport(id: $id) {
      id
      qty
      html
      factory {
        id
        name
      }
      product {
        id
        name
        number
        productLine {
          id
          name
        }
      }
      sharedWithCustomerAt
    }
  }
`;

export const SAVE_SIMPLE_REPORT = gql`
  mutation SAVE_SIMPLE_REPORT(
    $id: ID!
    $barcodeInnerboxEan13: String
    $barcodeInnerboxUpc: String
    $barcodeOuterboxEan13: String
    $barcodeOuterboxUpc: String
    $barcodeCarton: String
    $packageCheck: String
    $logoCheck: String
    $shareWithCustomer: Boolean
  ) {
    saveSimpleQcReport(
      id: $id
      barcodeInnerboxEan13: $barcodeInnerboxEan13
      barcodeInnerboxUpc: $barcodeInnerboxUpc
      barcodeOuterboxEan13: $barcodeOuterboxEan13
      barcodeOuterboxUpc: $barcodeOuterboxUpc
      barcodeCarton: $barcodeCarton
      packageCheck: $packageCheck
      logoCheck: $logoCheck
      shareWithCustomer: $shareWithCustomer
    ) {
      qcReport {
        ...qcReport
      }
    }
  }
  ${QC_REPORT_FRAGMENT}
`;

export const FETCH_DEFECT_CODES = gql`
  query FETCH_DEFECT_CODES {
    allDefectCodes {
      id
      code
      description
    }
  }
`;

export const DELETE_QC_REPORT = gql`
  mutation DELETE_QC_REPORT($id: ID!) {
    deleteQcReport(id: $id) {
      qcReport {
        id
        deletedAt
      }
    }
  }
`;

export const FETCH_BASE_INFO = gql`
  query FETCH_BASE_INFO {
    allInvoices {
      id
      number
      status
      odooModelType
    }
    factories: allFactories(isActive: true) {
      id
      name: nickName
    }
  }
`;

export const CREATE_FINAL_REPORT = gql`
  mutation CREATE_FINAL_REPORT(
    $factoryId: ID!
    $productId: ID!
    $qty: Int!
    $sourceType: String!
    $soOdooId: Int
    $soLineOdooId: Int
    $customerOdooId: Int
    $so: String
    $transferOdooId: Int
    $transferMoveOdooId: Int
    $transfer: String
    $invoiceId: ID
  ) {
    createFinalReport(
      factoryId: $factoryId
      productId: $productId
      qty: $qty
      sourceType: $sourceType
      soOdooId: $soOdooId
      soLineOdooId: $soLineOdooId
      customerOdooId: $customerOdooId
      so: $so
      transferOdooId: $transferOdooId
      transferMoveOdooId: $transferMoveOdooId
      transfer: $transfer
      invoiceId: $invoiceId
    ) {
      finalReport {
        id
      }
    }
  }
`;

export const CREATE_PO_REPORT = gql`
  mutation CREATE_PO_REPORT(
    $productId: ID!
    $qty: Int!
    $poOdooId: Int!
    $poLineOdooId: Int
    $po: String!
  ) {
    createPoReport(
      productId: $productId
      qty: $qty
      poOdooId: $poOdooId
      poLineOdooId: $poLineOdooId
      po: $po
    ) {
      poReport {
        id
      }
    }
  }
`;

export const DELETE_QC_ISSUE = gql`
  mutation DELETE_QC_ISSUE($id: ID!) {
    deleteQcIssue(id: $id) {
      qcReport {
        ...qcReport
      }
    }
  }
  ${QC_REPORT_FRAGMENT}
`;

export const FETCH_QC_TODO_LIST = gql`
  query FETCH_QC_TODO_LIST(
    $days: Int
    $warehouseIds: [Int]
    $limit: Int
    $forceRefresh: Boolean
  ) {
    qcTodoList(
      days: $days
      warehouseIds: $warehouseIds
      limit: $limit
      forceRefresh: $forceRefresh
    ) {
      id
      name
      sourceType
      deliveryDate
      lineCount
      orderLineIds
      customerName
      invoiceId
      invoiceNumber
    }
  }
`;

export const FETCH_QC_TODO_LINES_BULK = gql`
  query FETCH_QC_TODO_LINES_BULK(
    $lineSources: [QcTodoLineSourceInput]!
    $forceRefresh: Boolean
  ) {
    qcTodoLinesBulk(lineSources: $lineSources, forceRefresh: $forceRefresh) {
      id
      sourceType
      sourceId
      sourceName
      odooProductId
      odooProductName
      qty
      todoQty
      po
      customerOdooId
      invoiceId
      invoiceNumber
      product {
        id
        name
        number
        quantityPerCarton
        defaultSupplier {
          id
          name
        }
        packingFactory {
          id
          name
          odooId
        }
        computedImages(size: "300x300") {
          id
          url
        }
      }
    }
  }
`;

export const FETCH_QC_TODO_PO_LIST = gql`
  query FETCH_QC_TODO_PO_LIST($days: Int, $limit: Int, $forceRefresh: Boolean) {
    qcTodoPoList(days: $days, limit: $limit, forceRefresh: $forceRefresh) {
      id
      name
      vendorName
      plannedDate
      orderLineIds
    }
  }
`;

export const FETCH_QC_TODO_PO_LINES_BULK = gql`
  query FETCH_QC_TODO_PO_LINES_BULK($lineIds: [Int]!, $forceRefresh: Boolean) {
    qcTodoPoLinesBulk(lineIds: $lineIds, forceRefresh: $forceRefresh) {
      id
      poId
      poName
      odooProductName
      todoQty
      product {
        id
        name
        number
        defaultSupplier {
          name
        }
        computedImages(size: "300x300") {
          id
          url
        }
      }
    }
  }
`;
